import { useState } from "react";
import { Globe2, ShieldOff, MapPin, Mail, Bell, Loader2, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Phase 8.2 + 9 — Themed Geo-Block screen with lead-capture form.
 *
 * Rendered fullscreen by SaasAuthProvider when /saas/config/geo returns
 * {enforced: true, allowed: false}.
 *
 * Phase 9 lead capture: a small "tell me when you're available in my region"
 * form posts to /saas/geo-leads (public + middleware-exempt). On success we
 * show an inline confirmation card that converts the dead-end 403 into a
 * waitlist signup — operator can later contact these users when content
 * licensing expands.
 */
export default function GeoBlocked({ country, allowed_countries = [] }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr]  = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/saas/geo-leads", { email: email.trim().toLowerCase(), country });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Kayıt başarısız oldu. Lütfen tekrar deneyin.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="min-h-screen bg-bg text-white relative overflow-hidden"
      data-testid="geo-blocked-screen"
    >
      <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:60px_60px]" />
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-red-500/10 blur-3xl" />
      <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-amber-500/10 blur-3xl" />

      <div className="relative min-h-screen flex flex-col items-center justify-center px-6 py-16 max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-500/40 bg-red-500/10 mb-6" data-testid="geo-blocked-pill">
          <ShieldOff className="w-3.5 h-3.5 text-red-400" />
          <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-red-300">
            403 — Region Restricted
          </span>
        </div>

        <div className="relative mb-6">
          <div className="w-24 h-24 rounded-full bg-gold/10 border border-gold/30 grid place-items-center">
            <Globe2 className="w-12 h-12 text-gold" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-red-500/15 border border-red-500/40 grid place-items-center">
            <ShieldOff className="w-4 h-4 text-red-400" />
          </div>
        </div>

        <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter leading-[0.95] mb-4">
          Hizmetimiz bölgenizde<br />
          <span className="text-gold">kullanılamamaktadır.</span>
        </h1>

        <p className="text-base text-white/65 max-w-xl leading-relaxed mb-8">
          Lisans ve içerik dağıtım anlaşmalarımız gereği şu anda yalnızca
          belirli bölgelere hizmet sunabiliyoruz. Bu kısıtlama, IP adresinize
          göre algılanan konumunuz nedeniyle uygulanmıştır.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 max-w-md w-full mb-8">
          <div className="p-4 rounded-xl border border-white/10 bg-surface/60 text-left" data-testid="geo-blocked-detected">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-white/45 mb-1.5">
              <MapPin className="w-3 h-3" /> Algılanan Ülke
            </div>
            <div className="font-display font-bold text-3xl tracking-tight text-red-300">
              {country || "XX"}
            </div>
          </div>
          <div className="p-4 rounded-xl border border-gold/20 bg-gold/5 text-left">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-gold/70 mb-1.5">
              <Globe2 className="w-3 h-3" /> İzin Verilen Bölgeler
            </div>
            <div className="font-mono font-bold text-sm text-gold leading-snug" data-testid="geo-blocked-allowed">
              {allowed_countries.length > 0 ? allowed_countries.join(" · ") : "—"}
            </div>
          </div>
        </div>

        {/* ===== Phase 9 — Lead capture ===== */}
        <div className="w-full max-w-md p-6 rounded-2xl border border-gold/30 bg-surface/70 backdrop-blur-md text-left" data-testid="geo-lead-card">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4 text-gold" />
            <h3 className="font-display font-bold text-lg text-white">
              Bölgenize gelince haberin olsun
            </h3>
          </div>

          {!done ? (
            <>
              <p className="text-xs text-white/60 mb-4 leading-relaxed">
                E-posta adresinizi bırakın — içerik haklarımız {country || "bölgenize"} dahil
                bölgeye genişlediğinde size ilk biz haber verelim. Spam yok, sadece bir tek e-posta.
              </p>
              <form onSubmit={submit} className="space-y-3" data-testid="geo-lead-form">
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/35" />
                  <input
                    type="email"
                    required
                    placeholder="ornek@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-bg/80 border border-white/15 rounded-lg pl-10 pr-3 h-11 text-sm text-white placeholder:text-white/30 focus:border-gold/60 outline-none transition"
                    data-testid="geo-lead-email"
                  />
                </div>
                {err && (
                  <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2.5" data-testid="geo-lead-err">
                    {err}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={busy || !email}
                  className="w-full h-11 rounded-md bg-gold text-black font-bold text-sm tracking-tight hover:bg-gold-hover transition active:translate-y-[1px] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                  data-testid="geo-lead-submit"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
                  {busy ? "Kaydediliyor…" : "Beni Haberdar Et"}
                </button>
              </form>
              <p className="text-[10px] font-mono uppercase tracking-widest text-white/30 mt-3">
                E-posta sadece bilgilendirme amacıyla kullanılır · KVKK uyumlu
              </p>
            </>
          ) : (
            <div className="flex items-start gap-3 py-2" data-testid="geo-lead-done">
              <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/40 grid place-items-center shrink-0">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <div className="font-bold text-white text-base">Listemize eklendiniz</div>
                <p className="text-xs text-white/60 mt-0.5">
                  Bölgenize hizmet vermeye başladığımızda
                  {" "}<span className="text-gold font-mono">{email}</span>{" "}
                  adresine bildirim göndereceğiz.
                </p>
              </div>
            </div>
          )}
        </div>

        <a
          href="mailto:support@brand-name.tv"
          className="mt-6 inline-flex items-center gap-2 text-xs text-white/45 hover:text-gold transition-colors"
          data-testid="geo-blocked-contact"
        >
          <Mail className="w-3.5 h-3.5" />
          Destek Ekibiyle İletişime Geç
        </a>

        <p className="mt-4 text-[10px] font-mono uppercase tracking-widest text-white/25">
          Hatalı algılama olduğunu düşünüyorsanız VPN bağlantınızı kapatıp tekrar deneyin.
        </p>
      </div>
    </div>
  );
}
